import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import {
  clockInAttendance,
  clockOutAttendance,
  fetchAttendanceSessions,
  fetchMyAttendanceSessions,
} from '@/lib/api';
import { createStableId, readJsonStorage, storageKeys, writeJsonStorage } from '@/lib/storage';
import { useSessionState } from './SessionContext';
import { useStaffDirectoryState } from './StaffContext';
import type { AttendancePoint, AttendanceSession, StaffMember } from '@/lib/types';

type AttendanceContextValue = {
  isReady: boolean;
  staffMembers: readonly StaffMember[];
  sessions: AttendanceSession[];
  clockIn: (staffId: StaffMember['id'], location: AttendancePoint) => Promise<AttendanceSession>;
  clockOut: (staffId: StaffMember['id'], location: AttendancePoint) => Promise<AttendanceSession>;
  refreshAttendance: () => Promise<void>;
};

const AttendanceContext = createContext<AttendanceContextValue | null>(null);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isAttendancePoint(value: unknown): value is AttendancePoint {
  return (
    isRecord(value) &&
    typeof value.latitude === 'number' &&
    typeof value.longitude === 'number' &&
    (typeof value.accuracy === 'number' || value.accuracy === null)
  );
}

function isAttendanceSession(value: unknown): value is AttendanceSession {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.staffId === 'string' &&
    typeof value.staffName === 'string' &&
    typeof value.staffRole === 'string' &&
    typeof value.clockInAt === 'string' &&
    isAttendancePoint(value.clockInLocation) &&
    (typeof value.clockOutAt === 'string' || value.clockOutAt === null) &&
    (value.clockOutLocation === null || isAttendancePoint(value.clockOutLocation))
  );
}

function sortSessions(sessions: AttendanceSession[]) {
  return [...sessions].sort((left, right) => {
    return new Date(right.clockInAt).getTime() - new Date(left.clockInAt).getTime();
  });
}

function normalizeSessions(value: unknown): AttendanceSession[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return sortSessions(value.filter(isAttendanceSession));
}

function upsertSession(sessions: AttendanceSession[], nextSession: AttendanceSession) {
  return sortSessions([nextSession, ...sessions.filter((session) => session.id !== nextSession.id)]);
}

function mergeEnterpriseAttendance(apiSessions: AttendanceSession[], localSessions: AttendanceSession[]) {
  const merged = new Map<string, AttendanceSession>();

  for (const session of apiSessions) {
    merged.set(session.id, session);
  }

  for (const session of localSessions) {
    if (merged.has(session.id)) {
      continue;
    }

    const apiActiveForStaff = apiSessions.find(
      (item) => item.staffId === session.staffId && item.clockOutAt === null
    );

    if (apiActiveForStaff && session.clockOutAt === null) {
      continue;
    }

    merged.set(session.id, session);
  }

  return sortSessions([...merged.values()]);
}

export function AttendanceProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, sessionToken, sessionRole, sessionStaffId, sessionName, sessionUsername } = useSessionState();
  const { staffMembers: directoryStaffMembers } = useStaffDirectoryState();
  const [sessions, setSessions] = useState<AttendanceSession[]>([]);
  const [isReady, setIsReady] = useState(false);
  const loadedStorageKeyRef = useRef<string | null>(null);

  const staffMembers = useMemo(() => {
    if (sessionRole !== 'staff') {
      return directoryStaffMembers;
    }

    const currentStaff =
      directoryStaffMembers.find((member) => member.id === sessionStaffId) ??
      directoryStaffMembers.find(
        (member) => Boolean(sessionUsername) && member.username === sessionUsername
      );

    if (currentStaff) {
      return [currentStaff];
    }

    const ownAttendance = sessions[0] ?? null;

    return [
      {
        id: sessionStaffId ?? ownAttendance?.staffId ?? sessionUsername ?? 'current-staff',
        username: sessionUsername ?? '',
        name: sessionName ?? ownAttendance?.staffName ?? 'Staff',
        email: '',
        phoneNumber: '',
        password: '',
        role: ownAttendance?.staffRole ?? 'Staff',
        color: '#52525B',
        specialty: 'Your profile',
        bookings: 0,
        revenue: 0,
        retention: 0,
        onTime: 0,
        rating: 0,
      },
    ];
  }, [directoryStaffMembers, sessionName, sessionRole, sessionStaffId, sessionUsername, sessions]);

  const attendanceStorageKey = useMemo(() => {
    if (sessionRole === 'staff') {
      const identity = sessionStaffId ?? sessionUsername ?? 'current-staff';
      return `${storageKeys.attendance}:staff:${identity}`;
    }

    return storageKeys.attendance;
  }, [sessionRole, sessionStaffId, sessionUsername]);

  const refreshAttendance = useCallback(async () => {
    const stored = normalizeSessions(readJsonStorage(attendanceStorageKey));

    if (isAuthenticated && sessionToken) {
      try {
        const items =
          sessionRole === 'enterprise'
            ? await fetchAttendanceSessions(sessionToken)
            : await fetchMyAttendanceSessions(sessionToken);
        const merged =
          sessionRole === 'enterprise' ? mergeEnterpriseAttendance(items, stored) : sortSessions(items);

        loadedStorageKeyRef.current = attendanceStorageKey;
        setSessions(merged);
        writeJsonStorage(attendanceStorageKey, merged);
        return;
      } catch {
        // Fall through to local cache if the API is unavailable.
      }
    }

    loadedStorageKeyRef.current = attendanceStorageKey;
    setSessions(stored);
  }, [attendanceStorageKey, isAuthenticated, sessionRole, sessionToken]);

  useEffect(() => {
    let active = true;

    loadedStorageKeyRef.current = null;
    setIsReady(false);
    setSessions([]);

    (async () => {
      await refreshAttendance();

      if (active) {
        setIsReady(true);
      }
    })();

    return () => {
      active = false;
    };
  }, [refreshAttendance]);

  useEffect(() => {
    if (!isReady || loadedStorageKeyRef.current !== attendanceStorageKey) {
      return;
    }

    writeJsonStorage(attendanceStorageKey, sessions);
  }, [attendanceStorageKey, isReady, sessions]);

  const clockIn = useCallback(
    async (staffId: StaffMember['id'], location: AttendancePoint) => {
      if (!isReady) {
        throw new Error('Attendance is still loading.');
      }

      const staff = staffMembers.find((member) => member.id === staffId);
      const isSelfPunch = Boolean(sessionToken) && (sessionStaffId === staffId || sessionRole === 'staff');

      if (!staff && !isSelfPunch) {
        throw new Error('Unknown staff member.');
      }

      const staffName = staff?.name ?? sessionName ?? 'Staff member';
      const staffRole = staff?.role ?? 'Staff';

      const hasActiveSession = sessions.some(
        (session) => session.staffId === staffId && session.clockOutAt === null
      );

      if (hasActiveSession) {
        throw new Error(`${staffName} is already clocked in.`);
      }

      if (isSelfPunch && sessionToken) {
        const nextSession = await clockInAttendance(sessionToken, location);
        setSessions((current) => upsertSession(current, nextSession));
        return nextSession;
      }

      const nextSession: AttendanceSession = {
        id: createStableId('attendance'),
        staffId,
        staffName,
        staffRole,
        clockInAt: new Date().toISOString(),
        clockInLocation: location,
        clockOutAt: null,
        clockOutLocation: null,
      };

      setSessions((current) => sortSessions([nextSession, ...current]));
      return nextSession;
    },
    [isReady, sessionName, sessionRole, sessionStaffId, sessionToken, sessions, staffMembers]
  );

  const clockOut = useCallback(
    async (staffId: StaffMember['id'], location: AttendancePoint) => {
      if (!isReady) {
        throw new Error('Attendance is still loading.');
      }

      const staff = staffMembers.find((member) => member.id === staffId);
      const isSelfPunch = Boolean(sessionToken) && (sessionStaffId === staffId || sessionRole === 'staff');

      if (!staff && !isSelfPunch) {
        throw new Error('Unknown staff member.');
      }

      const staffName = staff?.name ?? sessionName ?? 'Staff member';

      const activeSession = sessions.find(
        (session) => session.staffId === staffId && session.clockOutAt === null
      );

      if (!activeSession) {
        throw new Error(`${staffName} does not have an active clock in.`);
      }

      if (isSelfPunch && sessionToken) {
        const updatedSession = await clockOutAttendance(sessionToken, location);
        setSessions((current) => upsertSession(current, updatedSession));
        return updatedSession;
      }

      const updatedSession: AttendanceSession = {
        ...activeSession,
        clockOutAt: new Date().toISOString(),
        clockOutLocation: location,
      };

      setSessions((current) =>
        sortSessions(current.map((session) => (session.id === activeSession.id ? updatedSession : session)))
      );

      return updatedSession;
    },
    [isReady, sessionName, sessionRole, sessionStaffId, sessionToken, sessions, staffMembers]
  );

  useEffect(() => {
    const onFocus = () => {
      void refreshAttendance();
    };

    window.addEventListener('focus', onFocus);

    return () => {
      window.removeEventListener('focus', onFocus);
    };
  }, [refreshAttendance]);

  const value = useMemo(
    () => ({
      isReady,
      staffMembers,
      sessions,
      clockIn,
      clockOut,
      refreshAttendance,
    }),
    [clockIn, clockOut, isReady, refreshAttendance, sessions, staffMembers]
  );

  return <AttendanceContext.Provider value={value}>{children}</AttendanceContext.Provider>;
}

export function useAttendanceState() {
  const context = useContext(AttendanceContext);

  if (!context) {
    throw new Error('useAttendanceState must be used within an AttendanceProvider');
  }

  return context;
}

export type { AttendancePoint, AttendanceSession };
